import React, { useState, useEffect } from 'react';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useSectionControl } from '../contexts/SectionControlContext';
import { useDraft } from '../contexts/DraftContext';
import Eye from 'lucide-react/dist/esm/icons/eye.js';
import EyeOff from 'lucide-react/dist/esm/icons/eye-off.js';
import ChevronUp from 'lucide-react/dist/esm/icons/chevron-up.js';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.js';
import { useTranslations } from '../contexts/TranslationContext';
import { getSectionLabelKey } from '../config/sectionRegistry';
import EditModeSectionConfig from './EditModeSectionConfig';

const SECTION_LABELS = {
  hero_section: 'Hero',
  hero: 'Hero',
  hero_carousel: 'Hero Carousel',
  image_slider: 'Image Slider',
  jackpot_card: 'Jackpot',
  jackpot_summary: 'Jackpot Summary',
  cards_remaining: 'Card Tracker',
  quick_stats: 'Quick Stats',
  how_it_works: 'How It Works',
  last_draw_info: 'Last Draw',
  bonus_draws: 'Bonus Draws',
  recent_winners: 'Recent Winners',
  compact_winners: 'Recent Winners',
  partner_banner: 'Partner Banner',
  charity_banner: 'Charity Banner',
  charity_impact: 'Charity Impact',
  charity_info_card: 'Charity Info',
  final_cta: 'Call to Action',
  live_draw: 'Live Draw',
  stats_winners: 'Stats & Winners',
  prize_summary: 'Prize Summary',
  elimination_tracker: 'Elimination Tracker',
  recent_eliminations: 'Recent Eliminations',
  countdown: 'Countdown',
  prize_breakdown: 'Prize Breakdown',
  draw_info: 'Draw Info',
  sidebar: 'Sidebar',
  leaderboard: 'Top Partners',
  rollover_timeline: 'Rollover Timeline',
  prize_gallery: 'Prize Gallery',
  jackpot_display: 'Jackpot Display',
  ball_tracker: 'Ball Tracker',
  session_info: 'Session Info',
  top_partners: 'Partner Showcase',
  fifty_split: '50/50 Split',
  spacer_1: 'Spacer 1',
  spacer_2: 'Spacer 2',
  spacer_3: 'Spacer 3',
  content_block_1: 'Content Block 1',
  content_block_2: 'Content Block 2',
  content_block_3: 'Content Block 3',
  organization_writeup: 'Organization Writeup',
  organization_writeup_2: 'Organization Writeup 2',
  recent_supporters: 'Recent Supporters',
  purchase_summary: 'Purchase Summary',
  ticket_packages: 'Ticket Packages',
  goal_thermometer: 'Fundraising Goal',
  purchase_hero: 'Purchase Hero',
  purchase_ticket_selector: 'Ticket Packages',
  purchase_weeks_selector: 'Weeks Selector',
  purchase_card_selector: 'Card Selector',
  purchase_partner_selector: 'Partner Selector',
  purchase_charity_card: 'Charity Card',
  purchase_payment: 'Payment Checkout',
  purchase_trust_footer: 'Trust Signals',
};

/**
 * Displays a section name badge with inline admin controls (hide/show, reorder).
 * Renders when editMode is true OR when the user is an admin.
 * When SectionControlContext is available, shows Eye and Up/Down buttons.
 * In preview mode: shows label badge only — no controls or config panels.
 */
export default function EditModeSectionLabel({ sectionType, label, editMode }) {
  const { t } = useTranslations();
  const { isAdmin } = useIsAdmin();
  const sectionControl = useSectionControl();
  const [busy, setBusy] = useState(false);

  // Check preview mode — suppress controls when previewing
  let isPreviewMode = false;
  try {
    const draft = useDraft();
    isPreviewMode = draft?.isPreviewMode || false;
  } catch {}

  // Auto-register unknown section types so every rendered section gets controls
  // MUST be before any early return to satisfy React hooks rules
  useEffect(() => {
    if (sectionType && sectionControl?.ensureSectionType && !isPreviewMode && (editMode || isAdmin)) {
      sectionControl.ensureSectionType(sectionType);
    }
  }, [sectionType, sectionControl, isPreviewMode, editMode, isAdmin]);

  if (!editMode && !isAdmin) return null;

  const displayLabel = t(getSectionLabelKey(sectionType), label || SECTION_LABELS[sectionType] || sectionType?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Section');
  const section = sectionControl?.getSectionByType?.(sectionType);
  const hasControls = isAdmin && section && sectionControl && !isPreviewMode;

  // Preview mode: show a subtle label badge only — no controls
  if (isPreviewMode) {
    return (
      <div
        className="absolute top-0 left-0 z-50 bg-amber-600/80 text-[10px] font-bold px-2 py-0.5 rounded-br-lg shadow-sm pointer-events-none select-none tracking-wide uppercase"
        style={{ color: '#ffffff' }}
      >
        {displayLabel}
      </div>
    );
  }

  if (!hasControls) {
    return (
      <>
        <div
          className="absolute top-0 left-0 z-50 bg-blue-600 text-[11px] font-bold px-2.5 py-1 rounded-br-lg shadow-md pointer-events-none select-none tracking-wide uppercase"
          style={{ color: '#ffffff' }}
        >
          {displayLabel}
        </div>
        <EditModeSectionConfig sectionType={sectionType} editMode={editMode} />
      </>
    );
  }

  const { isFirst, isLast } = sectionControl.getSectionPosition(sectionType);

  const handleToggle = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try { await sectionControl.toggleVisibility(section.id); } finally { setBusy(false); }
  };

  const handleUp = async (e) => {
    e.stopPropagation();
    if (busy || isFirst) return;
    setBusy(true);
    try { await sectionControl.moveUp(section.id); } finally { setBusy(false); }
  };

  const handleDown = async (e) => {
    e.stopPropagation();
    if (busy || isLast) return;
    setBusy(true);
    try { await sectionControl.moveDown(section.id); } finally { setBusy(false); }
  };

  return (
    <>
      <div
        className={`absolute top-0 left-0 z-50 bg-blue-600 text-[11px] font-bold rounded-br-lg shadow-md select-none tracking-wide uppercase flex items-center gap-0.5 ${busy ? 'opacity-60' : ''}`}
        style={{ color: '#ffffff' }}
      >
        <span className="px-2.5 py-1 pointer-events-none">{displayLabel}</span>
        <button
          onClick={handleToggle}
          className="p-1 hover:bg-blue-500 rounded transition-colors"
          title={section.is_visible !== false ? t('admin.sections.hideSection', 'Hide section') : t('admin.sections.showSection', 'Show section')}
        >
          {section.is_visible !== false ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5 opacity-60" />}
        </button>
        <button
          onClick={handleUp}
          disabled={isFirst}
          className="p-1 hover:bg-blue-500 rounded transition-colors disabled:opacity-30"
          title={t('admin.sections.moveUp', 'Move up')}
        >
          <ChevronUp className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={handleDown}
          disabled={isLast}
          className="p-1 hover:bg-blue-500 rounded transition-colors disabled:opacity-30 mr-1"
          title={t('admin.sections.moveDown', 'Move down')}
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </div>
      <EditModeSectionConfig sectionType={sectionType} editMode={editMode} />
    </>
  );
}
